/**
 * The dated, city-named first sentence of a timing hub (w3-timing-hubs).
 *
 * timing-answer.ts states concepts only, because it predates per-request
 * rendering. This module is its counterpart for the value itself: every time
 * it prints comes from the engine result the page already rendered (the
 * server's `initialResult`, or the tool's own re-fetch for another city or
 * date), so nothing here is authored or cached past its day. The hubs render
 * per request behind the IST-date HTML cache.
 */

import { nakshatras } from "@ishvaram/domain-data";

import { engineEndClock } from "./local-clock";

/** Classical Hindi names of the tithis, keyed by the engine's English name. */
const TITHI_HI: Readonly<Record<string, string>> = {
  Pratipada: "प्रतिपदा",
  Dwitiya: "द्वितीया",
  Tritiya: "तृतीया",
  Chaturthi: "चतुर्थी",
  Panchami: "पंचमी",
  Shashthi: "षष्ठी",
  Saptami: "सप्तमी",
  Ashtami: "अष्टमी",
  Navami: "नवमी",
  Dashami: "दशमी",
  Ekadashi: "एकादशी",
  Dwadashi: "द्वादशी",
  Trayodashi: "त्रयोदशी",
  Chaturdashi: "चतुर्दशी",
  Purnima: "पूर्णिमा",
  Amavasya: "अमावस्या",
};

const squash = (name: string) => name.toLowerCase().replace(/[^a-z]/g, "");

function nakshatraHi(name: string): string {
  const key = squash(name);
  const match = nakshatras.find(
    (n) => squash(n.name_en) === key || squash(n.id) === key || (key === "mula" && n.id === "moola")
  );
  return match?.name_hi ?? name;
}

export interface TimeWindow {
  start: string;
  end: string;
}

type Locale = "en" | "hi";

/** "Wednesday, 30 September 2026" / "बुधवार, 30 सितंबर 2026" for a YYYY-MM-DD date. */
export function longDate(isoDate: string, locale: Locale): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString(locale === "hi" ? "hi-IN" : "en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function when(isoDate: string, todayIso: string, locale: Locale): string {
  const date = longDate(isoDate, locale);
  if (locale === "hi") return isoDate === todayIso ? `आज (${date})` : date;
  return isoDate === todayIso ? `today (${date})` : `on ${date}`;
}

const span = (w: TimeWindow) => `${w.start}–${w.end}`;

export function rahuKaalTodayLine(
  result: {
    date: string;
    rahu_kaal: TimeWindow;
    yamagandam: TimeWindow;
    gulika_kaal: TimeWindow;
  },
  city: string,
  todayIso: string,
  locale: Locale
): string | null {
  if (!result.rahu_kaal || !result.yamagandam || !result.gulika_kaal) return null;
  const at = when(result.date, todayIso, locale);
  if (locale === "hi") {
    return (
      `${city} में ${at} राहु काल: ${span(result.rahu_kaal)} (IST)। ` +
      `यमगंड ${span(result.yamagandam)}, गुलिक काल ${span(result.gulika_kaal)}।`
    );
  }
  return (
    `Rahu Kaal (Rahu Kalam) ${at} in ${city}: ${span(result.rahu_kaal)} IST. ` +
    `Yamagandam ${span(result.yamagandam)}, Gulika Kaal ${span(result.gulika_kaal)}.`
  );
}

interface PanchangLineInput {
  date: string;
  sunrise: string;
  sunset: string;
  tithi: { name: string; end_time?: string | null; ends_at?: string | null };
  nakshatra: { name: string };
  rahu_kaal?: TimeWindow | null;
}

function hasPanchangFacts(result: Partial<PanchangLineInput>): result is PanchangLineInput {
  return Boolean(result.tithi?.name && result.nakshatra?.name && result.sunrise && result.sunset);
}

/**
 * " upto 21:05", or " upto 00:35 (next day)" when the tithi ends after the
 * day's midnight. No brackets around it: the day marker already carries one.
 */
function tithiUpto(tithi: PanchangLineInput["tithi"], date: string, locale: Locale): string {
  const end = engineEndClock(tithi, date, locale);
  if (!end) return "";
  return locale === "hi" ? ` ${end} तक` : ` upto ${end}`;
}

export function panchangTodayLine(
  result: PanchangLineInput,
  city: string,
  todayIso: string,
  locale: Locale
): string | null {
  // A partial result (an older engine, a test fixture) hides the line rather
  // than breaking the tool it sits above.
  if (!hasPanchangFacts(result)) return null;
  const at = when(result.date, todayIso, locale);
  const upto = tithiUpto(result.tithi, result.date, locale);
  const rahu = result.rahu_kaal ? span(result.rahu_kaal) : null;
  if (locale === "hi") {
    const tithi = TITHI_HI[result.tithi.name] ?? result.tithi.name;
    return (
      `${city} में ${at} का पंचांग: तिथि ${tithi}${upto}, नक्षत्र ${nakshatraHi(result.nakshatra.name)}, ` +
      `सूर्योदय ${result.sunrise}, सूर्यास्त ${result.sunset}${rahu ? `, राहु काल ${rahu}` : ""} (IST)।`
    );
  }
  return (
    `Panchang ${at} in ${city}: ${result.tithi.name} tithi${upto}, ${result.nakshatra.name} ` +
    `nakshatra, sunrise ${result.sunrise}, sunset ${result.sunset}${rahu ? `, Rahu Kaal ${rahu}` : ""} IST.`
  );
}

export function shubhMuhuratTodayLine(
  digest: {
    date: string;
    named_muhurats: Array<{ name_en: string; name_hi: string; start: string; end: string }>;
  },
  city: string,
  todayIso: string,
  locale: Locale
): string | null {
  const named = (digest.named_muhurats ?? []).slice(0, 3);
  if (named.length === 0) return null;
  const at = when(digest.date, todayIso, locale);
  const list = named
    .map((m) => `${locale === "hi" ? m.name_hi : m.name_en} ${m.start}–${m.end}`)
    .join(", ");
  return locale === "hi"
    ? `${city} में ${at} शुभ मुहूर्त: ${list} (IST)।`
    : `Shubh Muhurat ${at} in ${city}: ${list} IST.`;
}

export function choghadiyaTodayLine(
  result: {
    date: string;
    day_choghadiya: Array<{ name: string; name_hi?: string; start_time: string; end_time: string }>;
  },
  city: string,
  todayIso: string,
  locale: Locale
): string | null {
  // Amrit, Shubh and Labh are the auspicious day choghadiyas in every almanac.
  const good = (result.day_choghadiya ?? []).filter((p) =>
    /^(amrit|shubh|labh)$/i.test(p.name.trim())
  );
  if (good.length === 0) return null;
  const at = when(result.date, todayIso, locale);
  const list = good
    .map(
      (p) => `${locale === "hi" && p.name_hi ? p.name_hi : p.name} ${p.start_time}–${p.end_time}`
    )
    .join(", ");
  return locale === "hi"
    ? `${city} में ${at} दिन के शुभ चौघड़िया: ${list} (IST)।`
    : `Auspicious day Choghadiya ${at} in ${city}: ${list} IST.`;
}
